import Barchart from 'components/molecules/Barchart';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import Legend from 'components/molecules/Legend';
import { graphXAxisLabelFontSize } from 'components/organism/GrowthAnalysis/SalesAttributionDrilldownChart';
import {
    barWidth,
    legendData,
    salesGraphWidth,
} from 'components/organism/GrowthAnalysis/SalesChartContainer/SalesChartContainer';
import { commonClassName, commonHeaderClass } from 'components/pages/PromoScorecard';
import React, { FC, useContext, useMemo, useRef } from 'react';
import {
    calculateContainerHeight,
    formatNumWithKMBAlt,
    getAdditionalDataOnCopyClick,
    getConfig,
    scaleMaxValue,
    scaleMinValue,
} from 'utils/helperMethods';
import { categorySalesVsYaChartData } from 'utils/testData';
import { subHeaderClass, yAxisLabelClass } from '../GrowthAnalysis/ExpandedView';
import { yAxisWidth } from '../PriceLadder/BarPointChartContainer';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { priceCurveDrilldownChartDownload } from 'parser/graphToExcelDownloadParser';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { FilterContextData } from 'contexts/FilterContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PRICE_CURVE_CHART2_CLICKED } from 'utils/mixpanelEventNames';
import { UserContext } from 'contexts/ConfigContext';
import { ConfigKeys } from 'utils/Enums';

interface Props {
    xAxisSelectedValue: any;
    yAxisSelectedValue: any;
    levelsSelectedValue: any;
    selectedLegends: any[];
    selectedBarItem: any[];
    setSelectedBarItem: Function;
    data: any;
    loading: boolean;
    error: any;
    widthConfig: {
        containerWidth: number;
    };
}

const barChartContainerId = '';

const chartHeight = calculateContainerHeight(3, 300);

const PriceCurveBarChartContainer: FC<Props> = ({
    xAxisSelectedValue,
    yAxisSelectedValue,
    levelsSelectedValue,
    setSelectedBarItem,
    selectedLegends,
    selectedBarItem,
    widthConfig,
    data,
    loading,
    error,
}) => {
    //width config
    const { containerWidth } = widthConfig;
    const { appConfig } = useContext(UserContext);
    const { decimalForNonPercentage, decimalForPercentage } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);

    const chartCopyRef = useRef<any>();
    const { allCheckedAttribute, timeSelectionLabel } = useContext(FilterContextData);

    const graphViewContainerWidth =
        Math.max(data?.chartData?.data ? data?.chartData?.data?.length * salesGraphWidth : 0, containerWidth) ||
        window.innerWidth;
    const perGraphComponentWidth = graphViewContainerWidth / data?.chartData?.data?.length || 0;
    const mainLabel =
        selectedLegends?.length === 1
            ? `${yAxisSelectedValue?.label} Incentive by ${xAxisSelectedValue?.label} `
            : `${yAxisSelectedValue?.label} Index for ${selectedLegends?.[0]} vs ${selectedLegends?.[1]} `;

    function infiniteScrollOnNext(width: number, loading: boolean) {
        if (loading) return;
        //  console.log('logging asd', width, loading);
        // infiniteScrollWidthRef.current = width - 200;
        // getPriceLadderData([1, 2], offset + priceLadderConfig?.limit);
    }

    const chartExcelData = useMemo(() => {
        return priceCurveDrilldownChartDownload(data?.chartData?.data, xAxisSelectedValue?.label);
    }, [data?.chartData?.data, xAxisSelectedValue]);

    return (
        <section ref={chartCopyRef} className={`${commonClassName} mt-4`}>
            <div className="flex mb-4 justify-between items-center">
                <h2 className={`${subHeaderClass}`}>{mainLabel}</h2>
                {!loading && (
                    <div className="flex justify-center items-center gap-2">
                        <CopyToClipBoard refOfComponent={chartCopyRef} />
                        <DownloadExcel
                            title={'PriceCurveDrilldownChart'}
                            excelData={chartExcelData.data}
                            headers={chartExcelData.headers}
                        />
                    </div>
                )}
            </div>

            {loading ? (
                <div style={{ height: chartHeight + 50 }}>
                    <Loader />
                </div>
            ) : error ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: chartHeight + 50,
                    }}
                >
                    <ErrorBox msg={String(error)} />
                </div>
            ) : (
                <>
                    <Legend data={legendData} />
                    <p className={`${yAxisLabelClass} mb-4`}>Index</p>
                    <div className="flex">
                        <Barchart
                            // padding={[0, 0, 0, 48]}
                            padding={[0, 0, -2, 0]}
                            barWidth={30}
                            xAxisConfig={false}
                            dataset={[]}
                            height={chartHeight}
                            loading={false}
                            chartContainer={`price-curve-bar-y1`}
                            fields={['category', 'sales']}
                            //   minBound={scaleMinValue(40, tileChartData?.lineMaxVal)}
                            minBound={scaleMinValue(
                                15,
                                data?.chartData?.minVal - Math.abs(data?.chartData?.weightedAvg),
                            )}
                            maxBound={scaleMaxValue(10, data?.chartData?.maxVal)}
                            width={yAxisWidth}
                            colorKey={'color'}
                            yAxisLabelFormatter={(val: any) => {
                                return formatNumWithKMBAlt(
                                    Number(val) + data?.chartData?.weightedAvg,
                                    decimalForNonPercentage,
                                    decimalForNonPercentage,
                                );
                            }}
                            // Yline
                            YGrid={false}
                        />
                        <HorizontalInfiniteScroller
                            id={barChartContainerId}
                            hasMore={false} //subtracting 1 to remove total value which is the first item
                            scrollThreshold={0.9}
                            toScrollWidth={0}
                            onNext={(width: number) => {
                                infiniteScrollOnNext(width, false);
                            }}
                        >
                            <div id={barChartContainerId} className="overflow-x-auto w-full pb-[40px]">
                                <div className="flex">
                                    {data?.chartData?.data?.map((item: any, index: number) => {
                                        const isSelected = selectedBarItem?.[0]?.category === item?.category;
                                        return (
                                            <div className="">
                                                <div
                                                    className={
                                                        isSelected ? 'bg-bgBackground cursor-pointer' : 'cursor-pointer'
                                                    }
                                                    onClick={(e) => {
                                                        setSelectedBarItem(isSelected ? [] : [item]);
                                                        //mixpanel event
                                                        trackEventMixpanel(PRICE_CURVE_CHART2_CLICKED, {
                                                            itemClicked: item.category,
                                                            isSelected: !isSelected,
                                                        });
                                                    }}
                                                >
                                                    <Barchart
                                                        // padding={[0, 0, 0, 48]}
                                                        tooltipFormatter={(xAxis, val) => {
                                                            return {
                                                                name: xAxis,
                                                                value: `${formatNumWithKMBAlt(
                                                                    val,
                                                                    decimalForPercentage,
                                                                )}%`,
                                                            };
                                                        }}
                                                        intervalFormatter={(val: any) => {
                                                            return `${formatNumWithKMBAlt(val, decimalForPercentage)}%`;
                                                        }}
                                                        padding={[0, 0, 0, 0]}
                                                        barWidth={barWidth}
                                                        dataset={[item]}
                                                        height={chartHeight}
                                                        loading={false}
                                                        chartContainer={`price-curve-bar-main-${index}`}
                                                        fields={['category', 'sales']}
                                                        //   minBound={scaleMinValue(40, tileChartData?.lineMaxVal)}
                                                        minBound={scaleMinValue(
                                                            15,
                                                            data?.chartData?.minVal -
                                                                Math.abs(data?.chartData?.weightedAvg),
                                                        )}
                                                        maxBound={scaleMaxValue(10, data?.chartData?.maxVal)}
                                                        width={perGraphComponentWidth}
                                                        colorKey={'color'}
                                                        // Yline
                                                        // YGrid={false}
                                                    />
                                                </div>
                                                <div
                                                    style={{
                                                        width: perGraphComponentWidth,
                                                        minWidth: perGraphComponentWidth,
                                                        overflowWrap: 'anywhere',
                                                        fontSize: graphXAxisLabelFontSize,
                                                    }}
                                                    className="text-center p-[2px]"
                                                    key={item?.category}
                                                >
                                                    {item?.category}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </HorizontalInfiniteScroller>
                    </div>
                    <p className={`text-center !top-[-42px] mb-[-35px] relative ${yAxisLabelClass}`}>
                        {xAxisSelectedValue?.label}
                    </p>
                </>
            )}
        </section>
    );
};
export default PriceCurveBarChartContainer;
